// AdminPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LoadingBar from 'react-top-loading-bar';
import NewsTab from '../components/NewsTab';
import PatchNotesTab from '../components/PatchNotesTab';
import UsersTab from '../components/UsersTab';
import PostsTab from '../components/PostsTab';
import SurveyTab from "../components/SurveyTab";
import '../css/adminPage.css';
import LoadingSpinner from "../components/LoadingSpinner";
import CharactersTab from "../components/CharactersTab";
import api from "../api";

function AdminPage() {
    const { t } = useTranslation();
    const loadingBarRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [userRoles, setUserRoles] = useState([]);
    const [activeAdminTab, setActiveAdminTab] = useState('posts');

    const hasRole = (role) => userRoles.includes(role);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const accessToken = urlParams.get('access_token');

        if (accessToken) {
            localStorage.setItem('accessToken', accessToken);
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        const tokenFromStorage = localStorage.getItem('access_token');
        if (tokenFromStorage) {
            verifyAuthStatus(tokenFromStorage);
        } else {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated && userRoles.length > 0) {
            setActiveAdminTab(hasRole('ROLE_ADMIN') ? 'news' : 'posts');
        }
    }, [isAuthenticated, userRoles]);

    const verifyAuthStatus = async (token) => {
        try {
            if (loadingBarRef.current) {
                loadingBarRef.current.continuousStart();
            }

            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/auth/status`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.isAuthenticated) {
                setIsAuthenticated(true);
                setUserRoles(response.data.roles || []);
                setLoading(false);
            } else {
                setIsAuthenticated(false);
                setLoading(false);
                setErrorMessage(t('messages.unauthorizedAccess'));
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 401) {
                    localStorage.removeItem('access_token');
                    setIsAuthenticated(false);
                    setErrorMessage(t('messages.sessionExpired'));
                } else {
                    setErrorMessage(`Error: ${error.response.status} - ${error.response.statusText}`);
                }
            } else {
                setErrorMessage(t('messages.unauthorizedAccess'));
            }
            setLoading(false);
        } finally {
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <LoadingBar
                color="rgba(255, 0, 85, 1)"
                className="custom-loading-bar"
                ref={loadingBarRef}
            />
            <div className="admin-panel">
                <header className="admin-panel__header">
                <div className="admin-panel__eyebrow">Super People</div>
                <h1 className="admin-panel__title">Admin Panel</h1>
            </header>
            <div className="admin-panel__body">
            {isAuthenticated ? (
                <Tabs
                    activeKey={activeAdminTab}
                    onSelect={(k) => setActiveAdminTab(k)}
                    id="admin-tabs"
                    transition={false}
                >
                    {hasRole('ROLE_ADMIN') && (
                        <Tab eventKey="news" title={t('admin.news')}>
                            <NewsTab loadingBarRef={loadingBarRef} />
                        </Tab>
                    )}

                    {hasRole('ROLE_ADMIN') && (
                        <Tab eventKey="patchNotes" title={t('admin.patchNotes')}>
                            <PatchNotesTab loadingBarRef={loadingBarRef} />
                        </Tab>
                    )}

                    {hasRole('ROLE_ADMIN') && (
                        <Tab eventKey="users" title={t('admin.users')}>
                            <UsersTab
                                loadingBarRef={loadingBarRef}
                                isActive={activeAdminTab === 'users'}
                            />
                        </Tab>
                    )}

                    {(hasRole('ROLE_ADMIN') || hasRole('ROLE_MODERATOR')) && (
                        <Tab eventKey="posts" title={t('admin.posts')}>
                            <PostsTab loadingBarRef={loadingBarRef} hasRole={hasRole} />
                        </Tab>
                    )}

                    {hasRole('ROLE_ADMIN') && (
                        <Tab eventKey="characters" title={t('characters.title')}>
                            <CharactersTab loadingBarRef={loadingBarRef} />
                        </Tab>
                    )}

                    {hasRole('ROLE_ADMIN') && (
                        <Tab eventKey="survey" title={t('survey.title')}>
                            <SurveyTab loadingBarRef={loadingBarRef} />
                        </Tab>
                    )}
                </Tabs>
            ) : (
                <div className="session-expired">{errorMessage}</div>
            )}
            </div>
            </div>
        </>
    );
}

export default AdminPage;